import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, map, switchMap, take } from 'rxjs/operators';
import { selectVirtualsVerdict } from 'src/app/store/user-details/user-details.selectors';
import { IAccountVirtualsVerdict } from 'src/app/store/user-details/user-details.state';
import { environment } from 'src/environments/environment';
import { LocalStoreService } from '../services/local-store.service';
import { SettingsService } from '../services/settings.service';
import { VirtualsVerdictRefreshService } from '../services/virtuals-verdict-refresh.service';
import {
  isRefetchedVisibleFor,
  isStoredVerdictUsable,
} from '../services/virtuals-verdict';

// Gates the provider lobby (/virtuals) on three things, every one of which
// must say yes — and any error on the way says no (home):
//
//   1. the build-time kill switch `environment.providers.enabled` (plus a
//      complete config — see providersEnabled()). Production ships it ON so
//      the launch is a backend switch, not a frontend deploy: until 1b and 2
//      both say yes (PROVIDER_INTEGRATION_ARCHITECTURE §7 rollout order) the
//      route stays inert. Setting it false closes the section whatever the
//      backend says;
//   1b. the RUNTIME kill switch `virtualsEnabled` on /settings/getAllSettings
//      (SettingsService.virtualsEnabled$, design §4.6): the lobby can be
//      closed during a vendor incident with a settings write instead of a
//      frontend build + deploy. Both must be on (isVirtualsSectionOn); a
//      failed settings call reads as OFF. The server folds the same setting
//      into its verdict; this copy stays as defence in depth (design D6);
//   2. the SERVER's per-account verdict, `virtuals.visible` on
//      GET /user/getUserAccount (design D1/D2): role, account status, provider
//      state, currency allowlist, rollout rule and block, decided in one
//      place — the same verdict the nav entries read
//      (ProvidersVisibilityService), so a deep link and the menu can never
//      disagree. It used to check the role only, which walked an account
//      outside the rollout list into a lobby whose launch then 409'd.
//
// The verdict is taken from the store only when it is THIS account's (its
// fbId equals the stored login's uid) and younger than
// VIRTUALS_VERDICT_TTL_MS. Missing (deep link, cold store), foreign (the
// previous account's payload) or stale, it is refetched through the shared
// single-flight request (VirtualsVerdictRefreshService.refreshAccount) and the
// decision is made from that FRESH payload, not from whatever the store held —
// with one exception: a payload whose verdict is the backend's compute ERROR
// (it could not decide) is answered by the decision the store holds for this
// account, which the reducer has just kept in place of that ERROR
// (isRefetchedVisibleFor). A stored ERROR is never trusted from the store
// either (isStoredVerdictUsable), so a deep link always asks again on one.
// The guard runs on every entry to /virtuals, so this is also the "navigated
// to the lobby" refresh.
//
// SSR never renders the lobby: the launch URL is single-use, minted
// client-side at click time, and the login state this route depends on lives
// in the browser — the same reasoning as TndPageGuard. The client re-runs the
// guard after bootstrap.
@Injectable({ providedIn: 'root' })
export class ProvidersEnabledGuard implements CanActivate {
  constructor(
    private router: Router,
    private localStore: LocalStoreService,
    private ngrxStore: Store,
    private settingsService: SettingsService,
    private verdictRefresh: VirtualsVerdictRefreshService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  canActivate(): Observable<boolean | UrlTree> | UrlTree {
    const home = this.router.createUrlTree(['/']);

    if (!isPlatformBrowser(this.platformId) || !providersEnabled()) {
      return home;
    }

    const stored: any = this.localStore.getItem('user');
    if (!stored || !stored.user || !stored.user.uid) {
      return home;
    }
    const uid = String(stored.user.uid);

    const verdict$: Observable<boolean> = this.ngrxStore.select(selectVirtualsVerdict).pipe(
      take(1),
      switchMap((verdict: IAccountVirtualsVerdict) => {
        if (isStoredVerdictUsable(verdict, uid, Date.now())) {
          return of(verdict.visible === true);
        }
        return this.verdictRefresh.refreshAccount().pipe(
          take(1),
          // The store is read AFTER the answer: the shared request has
          // already dispatched it, so what the store holds now is what the
          // reducer made of it.
          switchMap((account: any) =>
            this.ngrxStore.select(selectVirtualsVerdict).pipe(
              take(1),
              map((now: IAccountVirtualsVerdict) => isRefetchedVisibleFor(account, now, uid))
            )
          )
        );
      }),
      // A request that completes without a payload is not a yes.
      defaultIfEmpty(false)
    );

    // Runtime switch first: it resolves from the settings call already in
    // flight for the layout (or fires it on a deep link) and answers false on
    // a failed call, so the verdict lookup only runs for a lobby that is open.
    return this.settingsService.virtualsRuntimeEnabledOnce().pipe(
      switchMap((runtimeOn: boolean) =>
        isVirtualsSectionOn(providersEnabled(), runtimeOn) ? verdict$ : of(false)
      ),
      map((allowed: boolean) => (allowed === true ? true : home)),
      catchError(() => of(home))
    );
  }
}

/**
 * The whole switch: the build-time config must be usable AND the runtime
 * flag must be an explicit `true`. Pure, so the guard, the nav entries and
 * the specs share one rule. The build-time flag stays the outer fail-closed
 * gate — a runtime `true` can never open a section the bundle shipped off.
 */
export function isVirtualsSectionOn(
  buildTimeUsable: boolean,
  runtimeEnabled: unknown
): boolean {
  return buildTimeUsable === true && runtimeEnabled === true;
}

/**
 * The one accessor for the build-time flag — guard, nav entries and page all
 * read this.
 *
 * `launcherOrigin` is part of the check on purpose. An empty one makes the
 * postMessage bridge inert by design, so a config with `enabled: true` and no
 * launcher origin would render a game that LOOKS alive and is not: no
 * `game.loaded`, no `game.resize.height` (stuck at the 480 px floor), no
 * `game.goto.home`, no balance refresh. Fail the whole section closed instead.
 *
 * This is the BUILD-TIME half only. The runtime half (`virtualsEnabled` on
 * `/settings/getAllSettings`, SettingsService.virtualsEnabled$) is ANDed in by
 * isVirtualsSectionOn() — the guard and ProvidersVisibilityService both go
 * through it, so a vendor incident is a settings write, not a frontend build +
 * Cloud Run deploy (the backend's own `provider_settings.enabled` kill switch
 * is a SQL update). The build-time flag stays the outer fail-closed gate.
 */
export function providersEnabled(): boolean {
  return isProvidersConfigUsable(environment.providers);
}

/** The rule behind providersEnabled(), pure so it can be driven both ways. */
export function isProvidersConfigUsable(providers: {
  apiUrl?: string;
  launcherOrigin?: string;
  enabled?: boolean;
} | null | undefined): boolean {
  return !!(
    providers &&
    providers.enabled &&
    providers.apiUrl &&
    providers.launcherOrigin
  );
}

/**
 * True for the provider lobby's own urls. Matched on the PATH only (query
 * and fragment stripped) and on whole segments, so '/virtualsomething' is a
 * different page, not a lobby. One definition for the layout (lobby mode)
 * and the lobby page (is this navigation leaving us?), so the two can never
 * disagree about where the lobby ends.
 */
export function isProviderLobbyUrl(url: string): boolean {
  const path = (url || '').split('?')[0].split('#')[0];
  return ['/virtuals', '/golden'].some(
    (base) => path === base || path.indexOf(`${base}/`) === 0
  );
}
